import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { EmployerService } from './employer.service';
import { ErrorHandlerService } from './error-handler.service';
import { ImplementAccountingCanIds } from '../enums/implement-accounting-can-ids-enum';
import { CostPerEnum } from '../enums/cost-per-enum';
import { ImplementAccountingValueModeEnum } from '../enums/implement-accounting-value-mode-enum';
import { ImplementAccountingModeEnum } from '../enums/implement-accounting-mode-enum';
import { WorkstateEnum } from '../enums/workstate-enum';
import { AccountingConfigDto, CanValueMappingDto, EmployerDto, ImplementV2Dto, ImplementRestService, ImplementIdDto } from '../api/exa';
import { AuthService } from './auth.service';
import { NGXLogger } from 'ngx-logger';
import { ExaGrant } from '../model/exa-grant';
import { HelperFunctions } from '../helper/helper-functions';
import { DDIService } from '../services/ddi.service';
import { AlertService } from './alert.service';
import { TranslateService } from '@ngx-translate/core';
@Injectable({
  providedIn: 'root',
})
export class ImplementService {
  readonly NO_IMPLEMENT: ImplementV2Dto = { id: -1 };

  private employerDto: EmployerDto = null;

  private implement_s: ImplementV2Dto[] = [];
  private implement_s$ = new BehaviorSubject<ImplementV2Dto[]>([]);
  private implement_sWD: ImplementV2Dto[] = [];
  private implement_sWD$ = new BehaviorSubject<ImplementV2Dto[]>([]);

  private implementsConfigRaw: any = null;

  private processLables: string[] = [];

  // Default language
  lng = 'de';

  constructor(
    private httpClient: HttpClient,
    private access: AuthService,
    private employerService: EmployerService,
    private implementRest: ImplementRestService,
    private errorHandler: ErrorHandlerService,
    private ddiService: DDIService,
    //private translate: TranslateService,
    private log: NGXLogger,
    private alertService: AlertService,
    private translate: TranslateService
  ) {}

  loadImplements() {
    // set currrent language
    //this.lng = this.translate.currentLang;
    this.log.debug('Language to use for pipes: ' + this.lng);
    this.loadImplementConfig();

    this.employerService.onSelectEmployer().subscribe((employer) => {
      this.employerDto = employer;
      if (this.employerDto) {
        this.reloadImplements();
      } else {
        this.implement_sWD = [];
        this.implement_s = [];
        this.implement_sWD$.next(this.implement_sWD);
        this.implement_s$.next(this.implement_s);
      }
    });
  }

  static isCombinedProcessImplement(type1: number, type2?: number, type3?: number): boolean {
    if (type1 == 4 || type1 == 11 || type1 == 14 || type1 == 18) {
      return true;
    } else if (type1 == 5 && (type2 == 1 || type2 == 2)) {
      return true;
    } else if (type1 == 6 && (type2 == 2 || type2 == 5)) {
      return true;
    } else if (type1 == 8 && (type2 == 1 || type2 == 3)) {
      return true;
    } else if (type1 == 10 && type2 == 5) {
      return true;
    } else if (type1 == 13 && (type2 == 5 || type2 == 6 || type2 == 12 || type2 == 15)) {
      return true;
    } else if (type1 == 15 && type2 == 6) {
      return true;
    } else if (type1 == 17 && type2 == 2) {
      return true;
    } else if (type1 == 19 && type2 == 1 && type3 == 1) {
      return true;
    }
    return false;
  }

  static isTransportImplement(type1: number, type2?: number, type3?: number): boolean {
    if (type1 == 10 && type2 != 5) {
      return true;
    }
    return false;
  }

  static isCombineImplement(type1: number, type2?: number, type3?: number): boolean {
    if (type1 == 15 && (type2 == 1 || type2 == 2)) {
      return true;
    }
    return false;
  }

  static isFieldworkImplement(type1: number, type2?: number, type3?: number): boolean {
    return (
      !this.isCombinedProcessImplement(type1, type2, type3) &&
      !this.isTransportImplement(type1, type2, type3) &&
      !this.isCombineImplement(type1, type2, type3)
    );
  }

  static isWorking(ws: WorkstateEnum | any): boolean {
    return (
      ws == WorkstateEnum.WORKING ||
      ws == WorkstateEnum.THRESHING ||
      ws == WorkstateEnum.THRESHING_WITH_STRAWCHOPPER ||
      ws == WorkstateEnum.THRESHING_UNLOADING ||
      ws == WorkstateEnum.THRESHING_WITH_STRAWCHOPPER_UNLOADING
    );
  }

  static isWaitingOnField(ws: WorkstateEnum | any): boolean {
    return ws == WorkstateEnum.MISC_FIELD || ws == WorkstateEnum.MISC_FIELD_GRAINTANK_FULL;
  }

  static isWaitingOnRoad(ws: WorkstateEnum | any): boolean {
    return ws == WorkstateEnum.MISC_ROAD || ws == WorkstateEnum.DRIVING_MISC;
  }

  static isDriving(ws: WorkstateEnum | any): boolean {
    return ws == WorkstateEnum.DRIVING || ws == WorkstateEnum.DRIVING_MISC;
  }

  static isTransporting(ws: WorkstateEnum | any): boolean {
    return (
      ws == WorkstateEnum.TRANSPORT_LOADED ||
      ws == WorkstateEnum.TRANSPORT_UNLOADED ||
      ws == WorkstateEnum.TRANSPORT_LOADING ||
      ws == WorkstateEnum.TRANSPORT_UNLOADING ||
      ws == WorkstateEnum.TRANSPORT_UNLOADING_DRIVING
    );
  }

  static isRoad(ws: WorkstateEnum | any): boolean {
    return ws == WorkstateEnum.ROAD || this.isWaitingOnRoad(ws) || this.isTransporting(ws) || this.isDriving(ws);
  }

  static isField(ws: WorkstateEnum | any): boolean {
    return ws == WorkstateEnum.TURNING || this.isWorking(ws) || this.isWaitingOnField(ws);
  }

  static isWaiting(ws: WorkstateEnum | any): boolean {
    return this.isWaitingOnRoad(ws) || this.isWaitingOnField(ws);
  }

  static getPossibleWorkstates(impl: ImplementV2Dto): WorkstateEnum[] {
    if (impl == null) {
      return this.deadheadWorkstates;
    } else if (this.isCombinedProcessImplement(impl.type1, impl.type2, impl.type3)) {
      return this.combinedProcessWorkstates;
    } else if (this.isTransportImplement(impl.type1, impl.type2, impl.type3)) {
      return this.transportWorkstates;
    } else if (this.isCombineImplement(impl.type1, impl.type2, impl.type3)) {
      return this.combineWorkstates;
    } else {
      //if(isFieldworkImplement(impl.type1, impl.type2, impl.type3)) {
      return this.fieldworkWorkstates;
    }
  }

  static getPossibleWorkstatesByType(type1?: number, type2?: number, type3?: number): WorkstateEnum[] {
    if (type1 == null) {
      return this.deadheadWorkstates;
    } else if (this.isCombinedProcessImplement(type1, type2, type3)) {
      return this.combinedProcessWorkstates;
    } else if (this.isTransportImplement(type1, type2, type3)) {
      return this.transportWorkstates;
    } else if (this.isCombineImplement(type1, type2, type3)) {
      return this.combineWorkstates;
    } else {
      //if(isFieldworkImplement(type1, type2, type3)) {
      return this.fieldworkWorkstates;
    }
  }

  reloadImplements() {
    this.implement_sWD = [];
    this.implement_s = [];
    if (this.employerDto && this.access.hasGrantOwner(ExaGrant.READ_IMPLEMENT, this.employerDto.id)) {
      this.implementRest.getImplementsUsingPOST(this.employerDto.username, { size: 200 }).subscribe(
        (implement_s) => {
          this.implement_sWD = implement_s.content;
          this.implement_s = this.filterDeletedElements(this.implement_sWD);
          this.implement_sWD$.next(this.implement_sWD);
          this.implement_s$.next(this.implement_s);
        },
        (err) => {
          this.implement_sWD = [];
          this.implement_s = [];
          this.implement_sWD$.next(this.implement_sWD);
          this.implement_s$.next(this.implement_s);
          this.errorHandler.handleResponseError(err);
        }
      );
    } else {
      this.implement_s$.next(this.implement_s);
      this.implement_sWD$.next(this.implement_sWD);
    }
  }

  onGetImplements(withDeleted?: boolean): Observable<ImplementV2Dto[]> {
    this.loadImplements();
    if (withDeleted) {
      return this.implement_sWD$;
    }
    return this.implement_s$;
  }

  getImplementsInactive(): ImplementV2Dto[] {
    return this.filterNonDeletedElements(this.implement_sWD);
  }

  sortImplements(impls: ImplementV2Dto[]): ImplementV2Dto[] {
    const ret = impls.sort(function (a, b) {
      if (!a.name || a.name.trim().length == 0) {
        return 1; // shift implements with no name to the end
      }
      if (!b.name || b.name.trim().length == 0) {
        return -1; // shift implements with no name to the end
      }
      return a.name.localeCompare(b.name);
    });
    return ret;
  }

  addEmptyElementFirst(impls: ImplementV2Dto[]): ImplementV2Dto[] {
    let ret = [...impls]; //copy
    ret.unshift(this.NO_IMPLEMENT);
    return ret;
  }

  getImplementById(implementId: number) {
    let implement_: ImplementV2Dto;
    if (implementId) {
      +this.implement_sWD.forEach((implement) => {
        if (implement.id == implementId) {
          implement_ = implement;
        } else {
          return null;
        }
      });

      return implement_;
    }
  }

  getProcessLabelById(implementId: number): string {
    if (!implementId) {
      return 'IMPLEMENT.PROCESS.0';
    }
    if (!this.processLables[implementId]) {
      const implConfig = this.getImplementConfigById(implementId);
      this.processLables[implementId] = implConfig.processLabel;
    }
    return this.processLables[implementId];
  }

   getProcessLabelImplement(implement: ImplementV2Dto | ImplementIdDto | undefined): string {
    let type1: number | undefined;
    let type2: number | undefined;
    let type3: number | undefined;
    if (implement) {
      type1 = implement.type1;
      type2 = implement.type2;
      type3 = implement.type3;
    }
    return this.getProcessLabel(type1, type2, type3);
  }

   getProcessLabel(type1?: number, type2?: number, type3?: number): string {
    if (type1 == null || type2 == null) {
      return 'IMPLEMENT.PROCESS.0';
    }
    const implConfig = this.getImplementConfig(type1, type2, type3);    
    return implConfig?.processLabel ?? 'IMPLEMENT.PROCESS.0';
  }
  

  getImplementConfigById(implementId: number): ImplementConfigDto {
    const impl = this.getImplementById(implementId);
    if (impl) {
      return this.getImplementConfig(impl.type1, impl.type2, impl.type3);
    } else {
      return this.getImplementConfig(null, null);
    }
  }

  getImplementConfig(type1: number, type2: number, type3?: number): ImplementConfigDto {
    const obj: ImplementConfigDto = { type1: type1, type2: type2, type3: type3, hasUnitWidth: false, hasUnitVolume: false, process: 0 };
    let tmp = null;
    if (this.implementsConfigRaw) {
      tmp = this.implementsConfigRaw;
      if (type1 && type1 > 0 && tmp.hasOwnProperty(type1)) {
        tmp = tmp[type1.toString()];
        if (type2 && type2 > 0 && tmp.hasOwnProperty(type2)) {
          tmp = tmp[type2.toString()];
          if (type3 && type3 > 0 && tmp.hasOwnProperty(type3)) {
            tmp = tmp[type3.toString()];
            // Set if Implement type3 supports unit width and/or unit volume
            if (tmp.hasOwnProperty('unit_width')) {
              obj.hasUnitWidth = true;
            }
            if (tmp.hasOwnProperty('unit_volume')) {
              obj.hasUnitVolume = true;
            }
          } else {
            // Set if Implement type2 supports unit width and/or unit volume
            if (tmp.hasOwnProperty('unit_width')) {
              obj.hasUnitWidth = true;
            }
            if (tmp.hasOwnProperty('unit_volume')) {
              obj.hasUnitVolume = true;
            }
          }
        } else {
          tmp = null;
        }
      } else {
        tmp = null;
      }
    }

    // if data found
    if (tmp) {
      if (tmp['process'] && tmp['process']['id']) {
        obj.process = tmp['process']['id'];
      }
    }
    obj.processLabel = 'IMPLEMENT.PROCESS.' + obj.process;
    return obj;
  }

  getImplementTypeCodes(type1?: number, type2?: number): ImplementTypeCodesDto {
    let levelKeys = null;
    let codeTranslationKey = 'IMPLEMENT.TYPE.';
    const lables: ImplementTypeCodesDto = { subTypeCodes: [], parentTypeCode1: type1, parentTypeCode2: type2 };
    if (this.implementsConfigRaw) {
      levelKeys = this.implementsConfigRaw;
      if (type1 && levelKeys.hasOwnProperty(type1)) {
        levelKeys = levelKeys[type1];
        codeTranslationKey = codeTranslationKey + type1 + '_';
        if (type2 && levelKeys.hasOwnProperty(type2)) {
          levelKeys = levelKeys[type2];
          codeTranslationKey = codeTranslationKey + type2 + '_';
        }
      }

      if (!levelKeys.hasOwnProperty('code')) {
        const keys = Object.keys(levelKeys);
        keys.forEach((key) => {
          const typeCode: ImplementTypeCodeDto = { code: parseInt(key), label: codeTranslationKey + key };
          lables.subTypeCodes.push(typeCode);
        });
      }
      //else leave blank -> no keys for this level -> end reached
    }
    return lables;
  }

  getAllowedWorkstates(type1?: number, type2?: number): WorkstateEnum[] {
    if (type1) {
      if (type1 == 10 && type2 != 5) {
        return ImplementService.transportWorkstates;
      } else if (
        type1 == 4 ||
        (type1 == 5 && (type2 == 1 || type2 == 2)) ||
        (type1 == 6 && (type2 == 1 || type2 == 2 || type2 == 5)) ||
        (type1 == 8 && (type2 == 1 || type2 == 3)) ||
        (type1 == 10 && type2 == 5) ||
        type1 == 11 ||
        (type1 == 13 && (type2 == 5 || type2 == 6 || type2 == 12 || type2 == 15)) ||
        type1 == 14 ||
        (type1 == 15 && type2 == 6) ||
        (type1 == 17 && type2 == 2) ||
        type1 == 18 ||
        (type1 == 19 && type2 == 1)
      ) {
        return ImplementService.combinedProcessWorkstates;
      } else if (type1 == 15 && (type2 == 1 || type2 == 2)) {
        return ImplementService.combineWorkstates;
      } else {
        return ImplementService.fieldworkWorkstates;
      }
    } else {
      return ImplementService.deadheadWorkstates;
    }
  }

  getFormattedNameById(implementId: number) {
    if (implementId) {
      const impl: ImplementV2Dto = this.getImplementById(implementId);
      if (impl) {
        return impl.name;
      }
    }
    return '';
  }

  getFormattedName(implement: ImplementV2Dto) {
    if (implement) {
      return implement.name;
    }
    return '';
  }

  getUnitOfAccountingId(implement: ImplementV2Dto, accountingConfig: AccountingConfigDto): string {
    if (!accountingConfig) {
      return null;
    }
    if (accountingConfig.valmode == ImplementAccountingValueModeEnum.TIME_IN_ACTIVE_STATE) {
      return 'h';
    }
    // check can value mappings
    if (implement && implement.canValueMappings) {
      const mapping: CanValueMappingDto = implement.canValueMappings[accountingConfig.id];
      if (mapping) {
        return mapping.parameterUnit;
      }
    }
    switch (accountingConfig.id) {
      case ImplementAccountingCanIds.Spe:
      case ImplementAccountingCanIds.WB_Speed:
      case ImplementAccountingCanIds.GB_Speed:
        return 'km/h';
      case ImplementAccountingCanIds.ENG_FueRat:
        return 'l/h';
      case ImplementAccountingCanIds.ENG_Spe:
      case ImplementAccountingCanIds.RPTO_Spe:
      case ImplementAccountingCanIds.FPTO_Spe:
        return 'rpm';
      case ImplementAccountingCanIds.ENG_Tor:
      case ImplementAccountingCanIds.RHitch_Pos:
      case ImplementAccountingCanIds.FHitch_Pos:
      case ImplementAccountingCanIds.Rear_NForce:
      case ImplementAccountingCanIds.Front_NForce:
      case ImplementAccountingCanIds.Yield_Mois:
      case ImplementAccountingCanIds.Sli:
        return '%';
      case ImplementAccountingCanIds.Rear_Draft:
      case ImplementAccountingCanIds.Front_Draft:
        return 'N';
      case ImplementAccountingCanIds.A1:
      case ImplementAccountingCanIds.A2:
      case ImplementAccountingCanIds.A3:
      case ImplementAccountingCanIds.A4:
      case ImplementAccountingCanIds.D1:
      case ImplementAccountingCanIds.D2:
      case ImplementAccountingCanIds.D3:
      case ImplementAccountingCanIds.D4:
      case ImplementAccountingCanIds.C1:
      case ImplementAccountingCanIds.C2:
        return '';
      case ImplementAccountingCanIds.ApplSlu:
        return 'm³';
      case ImplementAccountingCanIds.ApplAci:
      case ImplementAccountingCanIds.ApplAdd:
        return 'l';
      case ImplementAccountingCanIds.PHSlu:
        return 'pH';
      case ImplementAccountingCanIds.Chop_Act:
      case ImplementAccountingCanIds.Thresh_Drum:
      case ImplementAccountingCanIds.Cutter_State:
        return 'status';
      case ImplementAccountingCanIds.Yield_Act:
        return 't/ha';
      case ImplementAccountingCanIds.ApplRate_Setp:
        return 'l/ha';
      case ImplementAccountingCanIds.FlowRate:
        return 'l/min';
      default:
        return 'n.a.'; //this.ddiService.getUnitOfDDI(accountingConfig.id);
    }
  }

  convertAccountingValue(accountingConfig: AccountingConfigDto, accountingValue: number): number {
    if (!accountingConfig) {
      return null;
    }
    if (accountingConfig.valmode == ImplementAccountingValueModeEnum.TIME_IN_ACTIVE_STATE) {
      return accountingValue / 3600;
    }
    switch (accountingConfig.id) {
      case ImplementAccountingCanIds.Spe:
      case ImplementAccountingCanIds.WB_Speed:
      case ImplementAccountingCanIds.GB_Speed:
        return accountingValue * 3.6; // m/s -> km/h
      case ImplementAccountingCanIds.ENG_FueRat:
      case ImplementAccountingCanIds.ENG_Spe:
      case ImplementAccountingCanIds.RPTO_Spe:
      case ImplementAccountingCanIds.FPTO_Spe:
      case ImplementAccountingCanIds.ENG_Tor:
      case ImplementAccountingCanIds.RHitch_Pos:
      case ImplementAccountingCanIds.FHitch_Pos:
      case ImplementAccountingCanIds.Rear_NForce:
      case ImplementAccountingCanIds.Front_NForce:
      case ImplementAccountingCanIds.Rear_Draft:
      case ImplementAccountingCanIds.Front_Draft:
      case ImplementAccountingCanIds.Sli:
      case ImplementAccountingCanIds.A1:
      case ImplementAccountingCanIds.A2:
      case ImplementAccountingCanIds.A3:
      case ImplementAccountingCanIds.A4:
      case ImplementAccountingCanIds.D1:
      case ImplementAccountingCanIds.D2:
      case ImplementAccountingCanIds.D3:
      case ImplementAccountingCanIds.D4:
      case ImplementAccountingCanIds.C1:
      case ImplementAccountingCanIds.C2:
      case ImplementAccountingCanIds.ApplSlu:
      case ImplementAccountingCanIds.ApplAci:
      case ImplementAccountingCanIds.ApplAdd:
      case ImplementAccountingCanIds.PHSlu:
      case ImplementAccountingCanIds.Chop_Act:
      case ImplementAccountingCanIds.Thresh_Drum:
      case ImplementAccountingCanIds.Cutter_State:
      case ImplementAccountingCanIds.Yield_Act:
      case ImplementAccountingCanIds.Yield_Mois:
        return accountingValue;
      default:
        // also returns value if not a DDI ;)
        return accountingValue; //this.ddiService.convertDDIValue(accountingConfig.id, accountingValue);
    }
  }

  convertAccountingValueBack(accountingConfig: AccountingConfigDto, accountingValue: number): number {
    if (!accountingConfig) {
      return null;
    }
    if (accountingConfig.valmode == ImplementAccountingValueModeEnum.TIME_IN_ACTIVE_STATE) {
      return accountingValue * 3600;
    }
    switch (accountingConfig.id) {
      case ImplementAccountingCanIds.Spe:
      case ImplementAccountingCanIds.WB_Speed:
      case ImplementAccountingCanIds.GB_Speed:
        return accountingValue / 3.6; // m/s -> km/h
      case ImplementAccountingCanIds.ENG_FueRat:
      case ImplementAccountingCanIds.ENG_Spe:
      case ImplementAccountingCanIds.RPTO_Spe:
      case ImplementAccountingCanIds.FPTO_Spe:
      case ImplementAccountingCanIds.ENG_Tor:
      case ImplementAccountingCanIds.RHitch_Pos:
      case ImplementAccountingCanIds.FHitch_Pos:
      case ImplementAccountingCanIds.Rear_NForce:
      case ImplementAccountingCanIds.Front_NForce:
      case ImplementAccountingCanIds.Rear_Draft:
      case ImplementAccountingCanIds.Front_Draft:
      case ImplementAccountingCanIds.Sli:
      case ImplementAccountingCanIds.A1:
      case ImplementAccountingCanIds.A2:
      case ImplementAccountingCanIds.A3:
      case ImplementAccountingCanIds.A4:
      case ImplementAccountingCanIds.D1:
      case ImplementAccountingCanIds.D2:
      case ImplementAccountingCanIds.D3:
      case ImplementAccountingCanIds.D4:
      case ImplementAccountingCanIds.C1:
      case ImplementAccountingCanIds.C2:
      case ImplementAccountingCanIds.ApplSlu:
      case ImplementAccountingCanIds.ApplAci:
      case ImplementAccountingCanIds.ApplAdd:
      case ImplementAccountingCanIds.PHSlu:
      case ImplementAccountingCanIds.Chop_Act:
      case ImplementAccountingCanIds.Thresh_Drum:
      case ImplementAccountingCanIds.Cutter_State:
      case ImplementAccountingCanIds.Yield_Act:
      case ImplementAccountingCanIds.Yield_Mois:
        return accountingValue;
      default:
        // also returns value if not a DDI ;)
        return accountingValue; //this.ddiService.convertDDIValueBack(accountingConfig.id, accountingValue);
    }
  }

  translateAccountingId(accountingConfig: AccountingConfigDto): string {
    if (accountingConfig) {
      return accountingConfig.id;
      /*
      let translation: string = " - ";
      // translate can identifier if possible
      translation = this.translate.instant('ENUM.CAN_IDENTIFIER.' + accountingConfig.id);
      if (translation.startsWith('ENUM.')) {
        translation = accountingConfig.id;
      } else {
        return translation;
      }

      // translate event identifier if possible
      translation = this.translate.instant('ENUM.EVENTTYPE.' + accountingConfig.id);
      if (translation.startsWith('ENUM.')) {
        translation = accountingConfig.id;
      } else {
        return translation;
      }

      // translate ddi if possible
      const ddi = accountingConfig.id.split("_")[0];
      const elemNum = accountingConfig.id.split("_")[1];
      translation = this.translate.instant('ISOBUS.DDI.' + ddi);
      if (translation.startsWith('ISOBUS.')) {
        translation = accountingConfig.id;
      } else {
        // append ddi number to translation
        if(elemNum){
          return ddi + " (" + elemNum + ") " + translation;
        }else{
          return ddi + " " +  translation;
        }
      }

      return translation;
      */
    }
    return null;
  }

  accountingHasExtra(implement: ImplementV2Dto, accountingConfig: AccountingConfigDto): boolean {
    if (
      (accountingConfig && accountingConfig.mode == ImplementAccountingModeEnum.AVERAGE) ||
      accountingConfig.mode == ImplementAccountingModeEnum.MEDIAN
    ) {
      if (this.shouldNotHaveExtra(accountingConfig.id)) {
        return false;
      }
      const accountingUnit = this.getUnitOfAccountingId(implement, accountingConfig);
      if (accountingUnit) {
        return (
          accountingUnit.endsWith('/h') ||
          accountingUnit.endsWith('/min') ||
          accountingUnit.endsWith('/s') ||
          accountingUnit.endsWith('/ha') ||
          accountingUnit.endsWith('/m²') ||
          accountingUnit == 'l' ||
          accountingUnit == 'kg' ||
          accountingUnit == 'm³'
        );
      } else {
        return false;
      }
    }
    return false;
  }

  shouldNotHaveExtra(accountingId: string): boolean {
    return accountingId == '390';
  }

  getExtraUnitOfAccountingId(implement: ImplementV2Dto, accountingConfig: AccountingConfigDto): string {
    if (accountingConfig) {
      const accountingUnit = this.getUnitOfAccountingId(implement, accountingConfig);
      if (accountingUnit.endsWith('/h')) {
        return HelperFunctions.replaceAll(accountingUnit, '/h', '');
      }
      if (accountingUnit.endsWith('/min')) {
        return HelperFunctions.replaceAll(accountingUnit, '/min', '');
      }
      if (accountingUnit.endsWith('/s')) {
        return HelperFunctions.replaceAll(accountingUnit, '/s', '');
      }
      if (accountingUnit.endsWith('/ha')) {
        return HelperFunctions.replaceAll(accountingUnit, '/ha', '');
      }
      if (accountingUnit.endsWith('/m²')) {
        return HelperFunctions.replaceAll(accountingUnit, '/m²', '');
      }
      if (accountingUnit == 'l' || accountingUnit == 'kg' || accountingUnit == 'm³') {
        return accountingUnit + '/h';
      }
    } else {
      return null;
    }
  }

  getIconForCostConfig(costPerConfig: string) {
    if (costPerConfig) {
      switch (costPerConfig) {
        case CostPerEnum.H:
          return 'timer';
        case CostPerEnum.HA:
          return 'apps';
        case CostPerEnum.KM:
          return 'map';
      }
    }
  }

  addImplement(enterpriseUsername: string, implementInfo: ImplementV2Dto): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.implementRest.addImplementUsingPOST(enterpriseUsername, implementInfo).subscribe(
        (result) => {
          // Success
          this.alertService.openMessageToast(this.translate.instant('IMPLEMENT.ADD.SUCCESS'));
          console.log(' Implement hinzugefügt.');
          this.reloadImplements();
          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          reject(err);
        }
      );
    });
  }

  editImplement(implementId: number, implementInfo: ImplementV2Dto): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.implementRest.updateImplementUsingPUT(implementId, implementInfo).subscribe(
        (ret) => {
          this.alertService.openMessageToast(this.translate.instant('IMPLEMENT.EDIT.SUCCESS'));
          console.log(' Implement edit erfolgreich');
          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          reject(err);
        }
      );
    });
  }

  deleteImplement(implementId): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.implementRest.deleteImplementUsingDELETE(implementId).subscribe(
        (result) => {
          // Success
          this.alertService.openMessageToast(this.translate.instant('IMPLEMENT.DELETE.SUCCESS'));
          console.log(' implement delete -> SUCCESS');
          this.reloadImplements();
          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          console.log(' implement delete -> ERROR');
          reject(err);
        }
      );
    });
  }

  private filterDeletedElements(elementsWithDeleted: ImplementV2Dto[]): ImplementV2Dto[] {
    return elementsWithDeleted.filter((elem) => !elem.deleted);
  }

  private filterNonDeletedElements(elementsWithDeleted: ImplementV2Dto[]): ImplementV2Dto[] {
    return elementsWithDeleted.filter((elem) => elem.deleted);
  }

  private loadImplementConfig() {
    if (!this.implementsConfigRaw) {
      this.httpClient.get('./assets/ressources/implement_config.json').subscribe((data) => {
        this.implementsConfigRaw = data['implement_type'];
      });
    }
  }

  private sortString(n1, n2) {
    if (n1 > n2) {
      return 1;
    }
    if (n1 < n2) {
      return -1;
    }
    return 0;
  }

  private static fieldworkWorkstates: WorkstateEnum[] = [
    WorkstateEnum.PREPARATION,
    WorkstateEnum.ROAD,
    WorkstateEnum.MISC_ROAD,
    WorkstateEnum.WORKING,
    WorkstateEnum.TURNING,
    WorkstateEnum.MISC_FIELD,
  ];
  private static transportWorkstates: WorkstateEnum[] = [
    WorkstateEnum.PREPARATION,
    WorkstateEnum.TRANSPORT_UNLOADED,
    WorkstateEnum.TRANSPORT_LOADED,
    WorkstateEnum.TRANSPORT_LOADING,
    WorkstateEnum.TRANSPORT_UNLOADING,
    WorkstateEnum.MISC_ROAD,
    WorkstateEnum.MISC_FIELD,
  ];
  private static combinedProcessWorkstates: WorkstateEnum[] = [
    WorkstateEnum.PREPARATION,
    WorkstateEnum.ROAD,
    WorkstateEnum.MISC_ROAD,
    WorkstateEnum.WORKING,
    WorkstateEnum.TURNING,
    WorkstateEnum.TRANSPORT_LOADING,
    WorkstateEnum.TRANSPORT_UNLOADING,
    WorkstateEnum.MISC_FIELD,
  ];
  private static combineWorkstates: WorkstateEnum[] = [
    WorkstateEnum.PREPARATION,
    WorkstateEnum.ROAD,
    WorkstateEnum.MISC_ROAD,
    WorkstateEnum.TURNING,
    WorkstateEnum.THRESHING,
    WorkstateEnum.THRESHING_WITH_STRAWCHOPPER,
    WorkstateEnum.THRESHING_UNLOADING,
    WorkstateEnum.THRESHING_WITH_STRAWCHOPPER_UNLOADING,
    WorkstateEnum.TRANSPORT_UNLOADING,
    WorkstateEnum.TRANSPORT_UNLOADING_DRIVING,
    WorkstateEnum.MISC_FIELD,
    WorkstateEnum.MISC_FIELD_GRAINTANK_FULL,
  ];
  private static deadheadWorkstates: WorkstateEnum[] = [WorkstateEnum.DRIVING, WorkstateEnum.DRIVING_MISC];
}

export interface ImplementConfigDto {
  type1: number;
  type2: number;
  type3?: number;
  hasUnitWidth: boolean;
  hasUnitVolume: boolean;
  process: number;
  processLabel?: string;
}

export interface ImplementTypeCodesDto {
  parentTypeCode1: number;
  parentTypeCode2: number;
  subTypeCodes: ImplementTypeCodeDto[];
}

export interface ImplementTypeCodeDto {
  code: number;
  label: string;
}
